import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { useParams, useHistory, useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import { format, formatDistanceToNow } from 'date-fns'
import { IoLockClosedOutline, IoConstructOutline, IoDocumentTextOutline, IoArrowUpOutline, IoImageOutline, IoFilmOutline } from 'react-icons/io5'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import {
  Box,
  Collapse,
  Group,
  Paper,
  SimpleGrid
} from '@mantine/core'
import {
  AmmoScoreBadge,
  AppBadge,
  AppButton,
  AppCard,
  AppInput,
  AppModal,
  AppSkeleton,
  AppStack,
  AppTabs,
  AppText,
  AppTextarea,
  EmptyState,
  PostCard,
  TagPicker,
  ReportContentModal,
  LegalConsentCheckbox,
  TrustBadge
} from '../../app/common/ui'
import { ShootCard } from '../../components/FeedCards/ShootCard'
import type { ShootDto } from '../../components/FeedCards/ShootCard'
import type { RegistryCommentDto } from '../../app/models/registryComment'
import LoadingComponent from '../../app/layout/LoadingComponents'
import MainContentInset from '../../app/layout/MainContentInset'
import Breadcrumb from '../../app/common/Breadcrumb'
import { useStore } from '../../app/stores/store'
import agent from '../../app/api/agent'
import { FlagTargetType } from '../../app/models/flag'
import { useLegalText } from '../../app/hooks/useLegalText'
import type { RelatedModelDto } from '../../app/api/agent'
import { Post } from '../../app/models/post'
import { TagTargetType } from '../../app/models/tag'
import type { TagDto } from '../../app/models/tag'
import type { FirearmBuild } from '../../app/models/build'
import type { FirearmModelFormValues } from '../../app/models/firearmModel'
import { toAbsoluteMediaUrl } from '../../app/api/resolveApiBaseUrl'
import MobileImageCapture from '../../app/common/imageUpload/MobileImageCapture'
import SerialBlurModal from '../../app/common/imageUpload/SerialBlurModal'
import { ensureUploadableImageFile } from '../../app/common/imageUpload/ensureUploadableImageFile'
import AddToVaultButton from '../gunvault/AddToVaultButton'
import AddToWishlistButton from '../wishlist/AddToWishlistButton'
import CommunityPhotosGallery from '../contentPhotos/CommunityPhotosGallery'
import BuildCardThumbnail from '../builds/BuildCardThumbnail'
import { canCreateCommunityPost, MIN_AMMO_SCORE_TO_CREATE_POST } from '../../app/common/constants/reputation'

/** Must match backend AmmoScoreCalculator. */
const MIN_AMMO_SCORE_TO_EDIT_TAGS = 50
const MIN_AMMO_SCORE_TO_PROPOSE_EDIT = 100

function registryCommentInitials (displayName?: string | null, username?: string) {
  if (displayName?.trim()) {
    const parts = displayName.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    return displayName.slice(0, 2).toUpperCase()
  }
  if (username?.trim()) return username.trim().slice(0, 2).toUpperCase()
  return '?'
}

const STATUS_LABEL: Record<number, string> = {
  0: 'Pending approval',
  1: 'Approved',
  2: 'Rejected'
}

const editValidationSchema = Yup.object({
  make: Yup.string().required('Make is required').max(200, 'Max 200 characters'),
  model: Yup.string().required('Model is required').max(200, 'Max 200 characters'),
  platform: Yup.string().max(200),
  caliber: Yup.string().max(100),
  actionType: Yup.string().max(100),
  yearIntroduced: Yup.number().nullable().min(1800).max(2100),
  description: Yup.string().max(5000)
})

export default observer(function RegistryModelDetails () {
  const history = useHistory()
  const { firearmModelStore, userStore } = useStore()
  const {
    selectedModel,
    loadModel,
    loadingInitial,
    approveModel,
    rejectModel,
    deleteModel,
    clearSelectedModel,
    updateModel
  } = firearmModelStore
  const { id } = useParams<{ id: string }>()
  const location = useLocation<{ fromPost?: boolean; postId?: string; postTitle?: string }>()
  const fromPost = location.state?.fromPost
  const sourcePostId = location.state?.postId
  const sourcePostTitle = location.state?.postTitle

  const [reviews, setReviews] = useState<Post[]>([])
  const [ownershipTips, setOwnershipTips] = useState<Post[]>([])
  const [tags, setTags] = useState<TagDto[]>([])
  const [activeTab, setActiveTab] = useState<string>('overview')
  const [builds, setBuilds] = useState<FirearmBuild[]>([])
  const [buildsLoaded, setBuildsLoaded] = useState(false)
  const [buildsLoading, setBuildsLoading] = useState(false)
  const [videos, setVideos] = useState<ShootDto[]>([])
  const [videosLoaded, setVideosLoaded] = useState(false)
  const [videosLoading, setVideosLoading] = useState(false)
  const [modelStats, setModelStats] = useState<{ vaultCount: number; buildCount: number; postCount: number; avgVoteScore: number } | null>(null)
  const [relatedModels, setRelatedModels] = useState<RelatedModelDto[]>([])
  const [adminEditOpen, setAdminEditOpen] = useState(false)
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [hideModalOpen, setHideModalOpen] = useState(false)
  const [hideReason, setHideReason] = useState('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [changingCover, setChangingCover] = useState(false)
  const [coverConsentGiven, setCoverConsentGiven] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const { text: guidelinesText } = useLegalText('community-guidelines')
  const [coverModalOpen, setCoverModalOpen] = useState(false)
  const [coverUrlSectionOpen, setCoverUrlSectionOpen] = useState(false)
  const [coverUrlDraft, setCoverUrlDraft] = useState('')
  const [pendingCoverImageFile, setPendingCoverImageFile] = useState<File | null>(null)
  const [registryComments, setRegistryComments] = useState<RegistryCommentDto[]>([])
  const [registryCommentBody, setRegistryCommentBody] = useState('')
  const [registryCommentsLoading, setRegistryCommentsLoading] = useState(false)
  const [submittingRegistryComment, setSubmittingRegistryComment] = useState(false)

  useEffect(() => {
    if (id) loadModel(id)
    return () => clearSelectedModel()
  }, [id, loadModel, clearSelectedModel])

  useEffect(() => {
    if (!id) return
    agent.Posts.listByFirearmModel(id, 1).then(setReviews).catch(() => setReviews([]))
    agent.Posts.listByFirearmModel(id, 2).then(setOwnershipTips).catch(() => setOwnershipTips([]))
  }, [id])

  useEffect(() => {
    if (!id) return
    setRegistryCommentsLoading(true)
    agent.FirearmModels.listRegistryComments(id)
      .then((c) => setRegistryComments(c ?? []))
      .catch(() => setRegistryComments([]))
      .finally(() => setRegistryCommentsLoading(false))
  }, [id])

  useEffect(() => {
    if (!id) return
    agent.FirearmModels.getStats(id).then(setModelStats).catch(() => setModelStats(null))
    agent.FirearmModels.getRelated(id, 6).then(setRelatedModels).catch(() => setRelatedModels([]))
  }, [id])

  useEffect(() => {
    if (selectedModel?.tags != null) setTags(selectedModel.tags ?? [])
  }, [selectedModel?.tags])

  const breadcrumbItems = useMemo(() => {
    const base = [
      { label: 'Registry', href: '/registry' },
      {
        label: selectedModel
          ? `${selectedModel.make} ${selectedModel.model}`.trim() || 'Model'
          : 'Model'
      }
    ]
    if (fromPost && sourcePostId) {
      return [
        {
          label: sourcePostTitle ? `"${sourcePostTitle}"` : 'Back to post',
          href: `/blog/${sourcePostId}`
        },
        ...base
      ]
    }
    return base
  }, [selectedModel?.make, selectedModel?.model, fromPost, sourcePostId, sourcePostTitle])

  const handleApprove = async () => {
    if (!id) return
    try {
      await approveModel(id)
    } catch {
      // toast handled globally
    }
  }

  const handleRejectConfirm = async () => {
    if (!id) return
    try {
      await rejectModel(id, rejectReason.trim() || undefined)
      setRejectModalOpen(false)
      setRejectReason('')
    } catch {
      // toast handled globally
    }
  }

  const handleHideConfirm = async () => {
    if (!id) return
    try {
      await agent.FirearmModels.hide(id, hideReason.trim() || undefined)
      setHideModalOpen(false)
      setHideReason('')
      await loadModel(id)
    } catch {
      // toast globally
    }
  }

  const handleAddRegistryComment = async () => {
    if (!id || !registryCommentBody.trim()) return
    setSubmittingRegistryComment(true)
    try {
      const c = await agent.FirearmModels.addRegistryComment(id, registryCommentBody.trim())
      setRegistryComments((prev) => [...prev, c])
      setRegistryCommentBody('')
    } catch {
      // toast globally
    } finally {
      setSubmittingRegistryComment(false)
    }
  }

  if (firearmModelStore.viewError && !selectedModel) {
    return (
      <MainContentInset>
        <Box mb="md">
          <AppText
            component={Link}
            to={fromPost && sourcePostId ? `/blog/${sourcePostId}` : '/registry'}
            size="sm"
            sx={{ color: 'var(--gs-muted)', textDecoration: 'none', '&:hover': { color: 'var(--gs-text)' } }}
          >
            {fromPost && sourcePostId ? '← Back to post' : '← Back to Registry'}
          </AppText>
        </Box>
        <Box sx={{ padding: '24px', textAlign: 'center', color: 'var(--gs-muted)' }}>
          {firearmModelStore.viewError}
          {firearmModelStore.viewError?.includes('log in') && (
            <Box mt="md">
              <AppButton component={Link} to="/blog" variant="light">
                Go to log in
              </AppButton>
            </Box>
          )}
        </Box>
      </MainContentInset>
    )
  }

  if (loadingInitial || !selectedModel) {
    return <LoadingComponent content="Loading model..." />
  }

  const model = selectedModel

  const openCoverModal = () => {
    setCoverUrlDraft(model.coverImageUrl ?? '')
    setCoverUrlSectionOpen(false)
    setPendingCoverImageFile(null)
    setCoverConsentGiven(false)
    setCoverModalOpen(true)
  }

  const handleCoverFileSelected = (file: File) => {
    setPendingCoverImageFile(file)
  }

  const doCoverUpload = async (file: File) => {
    const modelId = id
    if (!modelId) return
    if (!coverConsentGiven) {
      toast.error('You must agree to the community guidelines before uploading.')
      return
    }
    let normalized: File
    try {
      normalized = await ensureUploadableImageFile(file)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not prepare image for upload.')
      return
    }
    try {
      setChangingCover(true)
      await firearmModelStore.uploadImage(modelId, normalized, {
        accepted: true,
        version: guidelinesText?.version ?? 1
      })
      toast.success('Cover image updated.')
      setCoverModalOpen(false)
      setPendingCoverImageFile(null)
    } catch {
      // toast via interceptor
    } finally {
      setChangingCover(false)
    }
  }

  const applyCoverUrl = async () => {
    const url = coverUrlDraft.trim()
    if (!id) return
    if (url && !/^https?:\/\//i.test(url)) {
      toast.error('URL must start with http:// or https://')
      return
    }
    try {
      setChangingCover(true)
      await updateModel(id, { coverImageUrl: url })
      toast.success(url ? 'Cover URL updated.' : 'Cover image cleared.')
      setCoverModalOpen(false)
    } catch {
      // toast via interceptor
    } finally {
      setChangingCover(false)
    }
  }

  const ammoScore = userStore.user?.ammoScore ?? 0
  const canEditRegistry =
    userStore.isLoggedIn &&
    (userStore.isAdmin ||
      userStore.isModerator ||
      (userStore.user?.username === model.submittedByUsername && model.status === 0) ||
      ammoScore >= MIN_AMMO_SCORE_TO_PROPOSE_EDIT)
  const canEditTags =
    userStore.isLoggedIn &&
    (userStore.isAdmin || userStore.isModerator || ammoScore >= MIN_AMMO_SCORE_TO_EDIT_TAGS)

  const isAdminOrMod = userStore.isAdmin || userStore.isModerator
  const canChangeCover = canEditRegistry
  const canReportModel =
    userStore.isLoggedIn &&
    userStore.user?.username !== model.submittedByUsername

  return (
    <MainContentInset pb={48}>
      <Box mb="md" sx={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <AppText
          component={Link}
          to={fromPost && sourcePostId ? `/blog/${sourcePostId}` : '/registry'}
          size="sm"
          sx={{ color: 'var(--gs-muted)', textDecoration: 'none', '&:hover': { color: 'var(--gs-text)' } }}
        >
          {fromPost && sourcePostId ? '← Back to post' : '← Back to Registry'}
        </AppText>
        <Breadcrumb items={breadcrumbItems} />
      </Box>

      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          height: 240,
          borderRadius: 12,
          overflow: 'hidden',
          marginBottom: 12,
          background: model.coverImageUrl
            ? undefined
            : 'linear-gradient(135deg, #0D1117, #1a0e05)'
        }}
      >
        {model.coverImageUrl
          ? (
          <img
            src={model.coverImageUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
            )
          : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#30363D'
            }}
          >
            <IoImageOutline size={48} />
          </Box>
            )}
        {canChangeCover && (
          <AppButton
            size="xs"
            sx={{
              position: 'absolute',
              top: 10,
              right: 12,
              background: 'rgba(13,17,23,0.75)',
              border: '1px solid var(--gs-border-bright)',
              color: 'var(--gs-text)',
              padding: '5px 10px',
              borderRadius: 5,
              fontSize: 11,
              fontWeight: 500,
              '&:hover': { background: 'rgba(22,27,34,0.9)' }
            }}
            onClick={openCoverModal}
          >
            Change Cover
          </AppButton>
        )}
        {canReportModel && (
          <AppButton
            size="xs"
            variant="outline"
            color="red"
            sx={{
              position: 'absolute',
              top: 10,
              left: 12,
              background: 'rgba(13,17,23,0.75)',
              borderColor: 'var(--gs-border-bright)',
              padding: '5px 10px',
              borderRadius: 5,
              fontSize: 11,
              fontWeight: 500
            }}
            onClick={() => setReportOpen(true)}
          >
            Report
          </AppButton>
        )}
      </Box>

      {/* Quick stats bar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          padding: '12px 24px',
          borderBottom: '1px solid var(--gs-border)',
          marginBottom: 16,
          background: 'var(--gs-surface)',
          borderRadius: 8
        }}
      >
        <Box sx={{ padding: '8px 20px 8px 0', marginRight: 20, borderRight: '1px solid var(--gs-border)' }}>
          <AppText size="xs" color="dimmed">
            Platform
          </AppText>
          <AppBadge size="sm" radius="sm" mt={4}>
            {model.platform || '—'}
          </AppBadge>
        </Box>
        <Box sx={{ padding: '8px 20px 8px 0', marginRight: 20, borderRight: '1px solid var(--gs-border)' }}>
          <AppText size="xs" color="dimmed">
            Caliber
          </AppText>
          <AppText size="sm" mt={4}>
            {model.caliber || '—'}
          </AppText>
        </Box>
        <Box sx={{ padding: '8px 20px 8px 0', marginRight: 20, borderRight: '1px solid var(--gs-border)' }}>
          <AppText size="xs" color="dimmed">
            Action Type
          </AppText>
          <AppText size="sm" mt={4}>
            {model.actionType || '—'}
          </AppText>
        </Box>
        <Box sx={{ padding: '8px 20px 8px 0', marginRight: 20, borderRight: '1px solid var(--gs-border)' }}>
          <AppText size="xs" color="dimmed">
            Year Introduced
          </AppText>
          <AppText size="sm" mt={4}>
            {model.yearIntroduced ?? '—'}
          </AppText>
        </Box>
        <Box sx={{ padding: '8px 20px 8px 0', marginRight: 20, borderRight: '1px solid var(--gs-border)' }}>
          <AppText size="xs" color="dimmed">
            Vote Score
          </AppText>
          <AppText size="sm" mt={4} sx={{ color: 'var(--gs-green)', fontWeight: 600 }}>
            ▲ {model.voteScore ?? 0}
          </AppText>
        </Box>
        <Box sx={{ padding: '8px 0 8px 0', marginRight: 20, borderRight: '1px solid var(--gs-border)' }}>
          <AppText size="xs" color="dimmed">
            Builds
          </AppText>
          <AppButton
            variant="subtle"
            size="xs"
            mt={2}
            sx={{ padding: 0, height: 'auto', fontSize: 12 }}
            onClick={() => history.push(`/builds?firearmModelId=${model.id}`)}
          >
            {model.buildCount ?? 0} build{(model.buildCount ?? 0) === 1 ? '' : 's'}
          </AppButton>
        </Box>
        <Box sx={{ padding: '8px 0 8px 0', display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <AddToVaultButton firearmModelId={model.id} />
          <AddToWishlistButton firearmModelId={model.id} />
        </Box>
      </Box>

      {/* Main layout */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 300px',
          gap: 16,
          alignItems: 'flex-start',
          '@media (max-width: 900px)': {
            gridTemplateColumns: 'minmax(0, 1fr)'
          }
        }}
      >
        {/* Main column */}
        <Box>
          <Box mb="sm">
            <AppText
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 28,
                textTransform: 'uppercase',
                color: 'var(--gs-text)'
              }}
            >
              {model.make} {model.model}
            </AppText>
            <Group spacing="xs" mt={4}>
              {model.submittedByUsername && (
                <AppText size="xs" color="dimmed">
                  Submitted by{' '}
                  <Link to={`/profiles/${model.submittedByUsername}`}>
                    @{model.submittedByUsername}
                  </Link>
                </AppText>
              )}
              <AppText size="xs" color="dimmed">
                · {format(new Date(model.createdAt), 'MMM d, yyyy')}
              </AppText>
              {model.approvedAt && model.status === 1 && (
                <AppText size="xs" color="dimmed">
                  · Approved {format(new Date(model.approvedAt), 'MMM d, yyyy')}
                </AppText>
              )}
            </Group>
            {model.isVerified && (
              <Box sx={{ marginTop: 8 }}>
                <TrustBadge variant="verified" label="Community Verified" />
              </Box>
            )}
          </Box>

          {/* ── Social proof strip ── */}
          {modelStats && (
            <Box
              sx={{
                display: 'flex',
                gap: 0,
                marginBottom: 16,
                background: 'var(--gs-surface2)',
                border: '1px solid var(--gs-border)',
                borderRadius: 8,
                overflow: 'hidden'
              }}
            >
              {[
                {
                  icon: <IoLockClosedOutline size={14} />,
                  value: modelStats.vaultCount,
                  label: 'own this',
                  tab: null,
                  color: '#C9A84C'
                },
                {
                  icon: <IoConstructOutline size={14} />,
                  value: modelStats.buildCount,
                  label: 'builds',
                  tab: 'builds',
                  color: 'var(--gs-blue)'
                },
                {
                  icon: <IoDocumentTextOutline size={14} />,
                  value: modelStats.postCount,
                  label: 'posts',
                  tab: 'reviews',
                  color: 'var(--gs-green)'
                },
                {
                  icon: <IoArrowUpOutline size={14} />,
                  value: modelStats.avgVoteScore > 0 ? `+${modelStats.avgVoteScore.toFixed(1)}` : modelStats.avgVoteScore.toFixed(1),
                  label: 'avg score',
                  tab: null,
                  color: 'var(--gs-accent)'
                }
              ].map(({ icon, value, label, tab, color }, i) => (
                <Box
                  key={label}
                  component={tab ? 'button' : 'div'}
                  type={tab ? 'button' : undefined}
                  onClick={tab ? () => setActiveTab(tab) : undefined}
                  sx={{
                    flex: 1,
                    padding: '12px 8px',
                    textAlign: 'center',
                    borderRight: i < 3 ? '1px solid var(--gs-border)' : 'none',
                    background: 'none',
                    cursor: tab ? 'pointer' : 'default',
                    transition: 'background 0.15s',
                    '&:hover': tab ? { background: 'rgba(232,93,4,0.06)' } : {}
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 2, color }}>
                    {icon}
                    <AppText component="span" sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 20, color, lineHeight: 1.1 }}>
                      {value}
                    </AppText>
                  </Box>
                  <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, color: 'var(--gs-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {label}
                  </AppText>
                </Box>
              ))}
            </Box>
          )}

          <AppTabs value={activeTab} onTabChange={(v) => setActiveTab(v ?? 'overview')}>
            <AppTabs.List>
              <AppTabs.Tab value="overview">Overview</AppTabs.Tab>
              <AppTabs.Tab value="reviews">Reviews</AppTabs.Tab>
              <AppTabs.Tab value="ownership">Ownership Tips</AppTabs.Tab>
              <AppTabs.Tab
                value="builds"
                onClick={async () => {
                  if (!id || buildsLoaded) return
                  setBuildsLoading(true)
                  try {
                    const result = await agent.Builds.list({
                      firearmModelId: id,
                      pageNumber: 1,
                      pageSize: 20
                    })
                    setBuilds(result.items ?? [])
                    setBuildsLoaded(true)
                  } catch {
                    setBuilds([])
                  } finally {
                    setBuildsLoading(false)
                  }
                }}
              >
                Builds
              </AppTabs.Tab>
              <AppTabs.Tab
                value="videos"
                onClick={async () => {
                  if (!id || videosLoaded) return
                  setVideosLoading(true)
                  try {
                    const rawFeed = await agent.SlopFeed.getMixedFeed(20, null, id)
                    const feedResult = rawFeed as any
                    const items = feedResult.items ?? []
                    const shootVideos = items
                      .filter((item: any) => item.type === 'Shoot' && item.mediaType === 'video')
                      .map((item: any) => ({
                        id: String(item.id),
                        userId: item.userId,
                        mediaUrl: toAbsoluteMediaUrl(item.mediaUrl),
                        mediaType: 'video' as const,
                        textBoxesJson: item.textBoxesJson ?? '',
                        createdAt: item.createdAt,
                        expiresAt: item.expiresAt,
                        userAvatar: item.userAvatarUrl ?? '',
                        userName: (item.userDisplayName || 'Member').trim(),
                        likeCount: item.likeCount ?? 0,
                        commentCount: item.commentCount ?? 0,
                        linkedCaption: item.firearmName ? `${item.firearmName}${item.cartridgeLabel ? ' · ' + item.cartridgeLabel : ''}` : undefined
                      } as ShootDto))
                    setVideos(shootVideos)
                    setVideosLoaded(true)
                  } catch {
                    setVideos([])
                  } finally {
                    setVideosLoading(false)
                  }
                }}
              >
                Videos
              </AppTabs.Tab>
            </AppTabs.List>

            <AppTabs.Panel value="overview" pt="md">
              {model.description
                ? (
                <AppText
                  sx={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 15,
                    lineHeight: 1.7,
                    whiteSpace: 'pre-wrap',
                    marginBottom: 16
                  }}
                >
                  {model.description}
                </AppText>
                  )
                : (
                <Box
                  sx={{
                    padding: '16px 20px',
                    marginBottom: 16,
                    background: 'var(--gs-surface2)',
                    border: '1px dashed var(--gs-border)',
                    borderRadius: 6
                  }}
                >
                  <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--gs-muted)', fontStyle: 'italic' }}>
                    No description yet for the {model.make} {model.model}.
                    {ammoScore >= MIN_AMMO_SCORE_TO_PROPOSE_EDIT
                      ? (
                      <>
                        {' '}
                        <AppText
                          component="span"
                          sx={{ color: 'var(--gs-accent)', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => setAdminEditOpen(true)}
                        >
                          Propose an edit →
                        </AppText>
                      </>
                        )
                      : (
                          ' Know this firearm? Reach Expert rank to propose an edit.'
                        )}
                  </AppText>
                </Box>
                  )}
              {id && (
                <Box mt="md" mb="md">
                  <TagPicker
                    targetType={TagTargetType.FirearmModel}
                    targetId={id}
                    currentTags={tags}
                    onTagApplied={(tag: TagDto) => setTags((prev) => [...prev, tag])}
                    onTagRemoved={(tag: TagDto) =>
                      setTags((prev) => prev.filter((t) => t.id !== tag.id))
                    }
                    readOnly={!canEditTags}
                    aiContext={selectedModel ? { name: `${selectedModel.make} ${selectedModel.model}`.trim(), description: selectedModel.description ?? undefined } : undefined}
                  />
                </Box>
              )}

              {/* Chambers section */}
              {selectedModel?.compatibleCartridges && (selectedModel.compatibleCartridges.official.length > 0 || selectedModel.compatibleCartridges.alternate.length > 0) ? (
                <Box mt="xl" pt="lg" sx={{ borderTop: '1px solid var(--gs-border)' }}>
                  <Group position="apart" mb="md">
                    <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 16 }}>
                      Chambers
                    </AppText>
                    {userStore.user?.isAdmin && (
                      <AppButton size="sm" variant="default" onClick={() => {
                        // TODO: Open add compatibility modal
                      }}>
                        + Add Caliber
                      </AppButton>
                    )}
                  </Group>

                  {selectedModel.compatibleCartridges.official.length > 0 && (
                    <Group spacing="xs" mb="md">
                      {selectedModel.compatibleCartridges.official.map((spec) => (
                        <AppBadge
                          key={spec.id}
                          component="a"
                          href={`/ammo/specs/${spec.id}`}
                          onClick={(e) => {
                            e.preventDefault()
                            history.push(`/ammo/specs/${spec.id}`)
                          }}
                          sx={{
                            cursor: 'pointer',
                            background: 'var(--gs-gold)',
                            color: '#000',
                            fontSize: 14,
                            padding: '8px 12px',
                            '&:hover': { opacity: 0.8 }
                          }}
                        >
                          {spec.name}
                        </AppBadge>
                      ))}
                    </Group>
                  )}

                  {selectedModel.compatibleCartridges.alternate.length > 0 && (
                    <Box>
                      <AppText size="sm" sx={{ color: 'var(--gs-muted)', marginBottom: '8px' }}>
                        Also compatible:
                      </AppText>
                      <Group spacing="xs">
                        {selectedModel.compatibleCartridges.alternate.map((spec) => (
                          <AppBadge
                            key={spec.id}
                            component="a"
                            href={`/ammo/specs/${spec.id}`}
                            onClick={(e) => {
                              e.preventDefault()
                              history.push(`/ammo/specs/${spec.id}`)
                            }}
                            sx={{
                              cursor: 'pointer',
                              background: 'var(--gs-surface2)',
                              color: 'var(--gs-muted)',
                              fontSize: 12,
                              padding: '6px 10px',
                              '&:hover': { color: 'var(--gs-text)' }
                            }}
                            variant="outline"
                          >
                            {spec.name}
                          </AppBadge>
                        ))}
                      </Group>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box mt="xl" pt="lg" sx={{ borderTop: '1px solid var(--gs-border)' }}>
                  <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 16, marginBottom: 12 }}>
                    Chambers
                  </AppText>
                  <AppText size="sm" sx={{ color: 'var(--gs-muted)', fontStyle: 'italic' }}>
                    Compatibility data not yet added.
                  </AppText>
                </Box>
              )}

              {id && <CommunityPhotosGallery entityType="FirearmModel" entityId={id} />}
              <Box mt="xl" pt="lg" sx={{ borderTop: '1px solid var(--gs-border)' }}>
                <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 16, marginBottom: 12 }}>
                  Model discussion
                </AppText>
                <AppText size="sm" color="dimmed" mb="md" sx={{ fontFamily: 'DM Sans, sans-serif' }}>
                  Short notes and questions about this entry. For long-form content, use reviews or ownership tips.
                </AppText>
                {userStore.isLoggedIn
                  ? (
                  <>
                    <AppTextarea
                      placeholder="Add a note about this model…"
                      value={registryCommentBody}
                      onChange={(e) => setRegistryCommentBody(e.currentTarget.value)}
                      minRows={2}
                      mb="sm"
                      sx={{ fontFamily: 'DM Sans, sans-serif' }}
                    />
                    <AppButton
                      size="sm"
                      color="ember"
                      onClick={() => void handleAddRegistryComment()}
                      loading={submittingRegistryComment}
                      disabled={!registryCommentBody.trim()}
                    >
                      Post
                    </AppButton>
                  </>
                    )
                  : (
                  <AppText size="sm" color="dimmed" mb="md">
                    <AppText component={Link} to="/login" sx={{ color: 'var(--gs-accent)' }}>
                      Sign in
                    </AppText>{' '}
                    to join the discussion.
                  </AppText>
                    )}
                <Box mt="md">
                  {registryCommentsLoading
                    ? (
                    <AppText size="sm" color="dimmed">
                      Loading comments…
                    </AppText>
                      )
                    : registryComments.length === 0
                      ? (
                    <AppText size="sm" color="dimmed" sx={{ fontStyle: 'italic' }}>
                      No comments yet.
                    </AppText>
                        )
                      : (
                          registryComments.map((c) => (
                      <Group key={c.id} align="flex-start" spacing="sm" mb="md" noWrap>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: c.authorImage ? `url(${c.authorImage}) center/cover` : 'var(--gs-surface2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 11,
                            fontWeight: 600,
                            flexShrink: 0
                          }}
                        >
                          {!c.authorImage && registryCommentInitials(c.authorDisplayName, c.authorUsername)}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Group spacing="xs">
                            <AppText component={Link} to={`/profiles/${c.authorUsername}`} size="sm" weight={500}>
                              {c.authorDisplayName || c.authorUsername}
                            </AppText>
                            <AmmoScoreBadge tier={c.authorAmmoTier ?? 'Newcomer'} score={c.authorAmmoScore ?? 0} size="sm" />
                            <AppText size="xs" color="dimmed">
                              {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
                            </AppText>
                          </Group>
                          <AppText size="sm" sx={{ marginTop: 4, fontFamily: 'DM Sans, sans-serif' }}>
                            {c.body}
                          </AppText>
                        </Box>
                      </Group>
                          ))
                        )}
                </Box>
              </Box>
            </AppTabs.Panel>

            <AppTabs.Panel value="reviews" pt="md">
              {reviews.length === 0
                ? (
                <EmptyState
                  title="No reviews yet"
                  message="Be the first to review this model."
                  action={{
                    label: 'Write a Review',
                    onClick: () => {
                      if (!userStore.isLoggedIn) {
                        history.push('/login')
                        return
                      }
                      if (!canCreateCommunityPost(userStore.user)) {
                        toast.info(
                          `Reach ${MIN_AMMO_SCORE_TO_CREATE_POST} AmmoScore to publish reviews and other posts. Vote and comment to earn score.`,
                          { autoClose: 6000 }
                        )
                        return
                      }
                      history.push(`/createPost?firearmModelId=${model.id}&type=1`)
                    }
                  }}
                />
                  )
                : (
                <Box>
                  {reviews.map((post) => (
                    <Box key={post.id} mb="sm">
                      <PostCard
                        id={post.id}
                        title={post.title}
                        excerpt={post.excerpt}
                        type={post.type}
                        coverImageUrl={post.coverImageUrl}
                        mainPhotoUrl={post.mainPhotoUrl}
                        authorUsername={post.authorUsername}
                        authorDisplayName={post.authorDisplayName}
                        authorImage={post.authorImage}
                        authorTier={post.authorAmmoTier}
                        authorScore={post.authorAmmoScore}
                        voteScore={post.voteScore}
                        currentUserVote={post.currentUserVote}
                        commentCount={post.comments?.length ?? 0}
                        createdAt={post.createdAt}
                        variant="compact"
                      />
                    </Box>
                  ))}
                </Box>
                  )}
            </AppTabs.Panel>

            <AppTabs.Panel value="ownership" pt="md">
              {ownershipTips.length === 0
                ? (
                <EmptyState
                  title="No ownership tips yet"
                  message="Share what it's like to own this model."
                  action={{
                    label: 'Share an Ownership Tip',
                    onClick: () => {
                      if (!userStore.isLoggedIn) {
                        history.push('/login')
                        return
                      }
                      if (!canCreateCommunityPost(userStore.user)) {
                        toast.info(
                          `Reach ${MIN_AMMO_SCORE_TO_CREATE_POST} AmmoScore to publish tips and other posts. Vote and comment to earn score.`,
                          { autoClose: 6000 }
                        )
                        return
                      }
                      history.push(`/createPost?firearmModelId=${model.id}&type=2`)
                    }
                  }}
                />
                  )
                : (
                <Box>
                  {ownershipTips.map((post) => (
                    <Box key={post.id} mb="sm">
                      <PostCard
                        id={post.id}
                        title={post.title}
                        excerpt={post.excerpt}
                        type={post.type}
                        coverImageUrl={post.coverImageUrl}
                        mainPhotoUrl={post.mainPhotoUrl}
                        authorUsername={post.authorUsername}
                        authorDisplayName={post.authorDisplayName}
                        authorImage={post.authorImage}
                        authorTier={post.authorAmmoTier}
                        authorScore={post.authorAmmoScore}
                        voteScore={post.voteScore}
                        currentUserVote={post.currentUserVote}
                        commentCount={post.comments?.length ?? 0}
                        createdAt={post.createdAt}
                        variant="compact"
                      />
                    </Box>
                  ))}
                </Box>
                  )}
            </AppTabs.Panel>

            <AppTabs.Panel value="builds" pt="md">
              {buildsLoading
                ? (
                <LoadingComponent content="Loading builds..." />
                  )
                : builds.length === 0
                  ? (
                <EmptyState
                  title="No builds yet"
                  message="Create a build that uses this model."
                  action={{
                    label: 'Create a Build',
                    onClick: () =>
                      history.push(`/builds/new?firearmModelId=${model.id}`)
                  }}
                />
                    )
                  : (
                <Box>
                  {builds.map((b) => (
                    <AppCard
                      key={b.id}
                      withBorder
                      padding="sm"
                      mb="xs"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => history.push(`/builds/${b.id}`)}
                    >
                      <Group align="flex-start" spacing="sm" noWrap>
                        <BuildCardThumbnail url={b.mainPhotoUrl} alt={b.name} variant="compact" />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <AppText weight={600} size="sm">
                            {b.name}
                          </AppText>
                          <AppText size="xs" color="dimmed">
                            by {b.ownerUsername} · {b.parts?.length ?? 0} parts · ▲{' '}
                            {b.voteScore ?? 0}
                          </AppText>
                        </Box>
                      </Group>
                    </AppCard>
                  ))}
                </Box>
                    )}
            </AppTabs.Panel>

            <AppTabs.Panel value="videos" pt="md">
              {videosLoading
                ? (
                <LoadingComponent content="Loading videos..." />
                  )
                : videos.length === 0
                  ? (
                <EmptyState
                  icon={<IoFilmOutline size={48} />}
                  title="No videos yet"
                  subtitle="No videos have been linked to this firearm model. Be the first to upload one!"
                  action={
                    userStore.isLoggedIn ? (
                      <AppButton size="sm" onClick={() => window.location.href = '/slop-feed'}>
                        Upload a video
                      </AppButton>
                    ) : null
                  }
                />
                    )
                  : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'md' }}>
                  {videos.map((video) => (
                    <ShootCard
                      key={video.id}
                      shoot={video}
                      onShare={() => {
                        const url = `${window.location.origin}/slop-feed/shoot/${video.id}`
                        if (navigator.share) {
                          void navigator.share({ title: 'GunSlop Video', url }).catch(() => undefined)
                        } else {
                          void navigator.clipboard.writeText(url)
                        }
                      }}
                    />
                  ))}
                </Box>
                    )}
            </AppTabs.Panel>

          </AppTabs>
        </Box>

        {/* Sidebar */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Community stats */}
          <AppCard withBorder padding="md">
            <AppText
              size="xs"
              color="dimmed"
              sx={{ fontFamily: 'DM Sans, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}
              mb={8}
            >
              Community
            </AppText>
            {modelStats
              ? (
              <Box>
                {[
                  ['Vault owners', modelStats.vaultCount],
                  ['Builds', modelStats.buildCount],
                  ['Posts & reviews', modelStats.postCount],
                  ['Avg vote score', modelStats.avgVoteScore]
                ].map(([label, value]) => (
                  <Group key={label as string} position="apart" spacing="xs" mb={4}>
                    <AppText size="xs" color="dimmed">{label}</AppText>
                    <AppText size="xs" weight={600} sx={{ color: 'var(--gs-accent)' }}>{value}</AppText>
                  </Group>
                ))}
              </Box>
                )
              : (
              <Box>
                {[1, 2, 3, 4].map((n) => (
                  <Group key={n} position="apart" mb={4}>
                    <AppSkeleton height={10} width="45%" />
                    <AppSkeleton height={10} width="20%" />
                  </Group>
                ))}
              </Box>
                )}
          </AppCard>

          <AppCard withBorder padding="md">
            <AppText
              size="xs"
              color="dimmed"
              sx={{ fontFamily: 'DM Sans, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}
              mb={6}
            >
              Quick specs
            </AppText>
            <Box>
              {[
                ['Make', model.make],
                ['Model', model.model],
                ['Platform', model.platform || '—'],
                ['Caliber', model.caliber || '—'],
                ['Action Type', model.actionType || '—'],
                ['Year Introduced', model.yearIntroduced ?? '—']
              ].map(([label, value]) => (
                <Group key={label as string} position="apart" spacing="xs" mb={4}>
                  <AppText size="xs" color="dimmed">
                    {label}
                  </AppText>
                  <AppText size="xs" weight={600}>
                    {value as string | number}
                  </AppText>
                </Group>
              ))}
            </Box>
          </AppCard>

          <AppCard withBorder padding="md">
            <AppText
              size="xs"
              color="dimmed"
              sx={{ fontFamily: 'DM Sans, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}
              mb={6}
            >
              Submission info
            </AppText>
            <Box>
              {model.submittedByUsername && (
                <Group position="apart" spacing="xs" mb={4}>
                  <AppText size="xs" color="dimmed">
                    Submitted by
                  </AppText>
                  <Link to={`/profiles/${model.submittedByUsername}`}>
                    <AppText size="xs" weight={600}>
                      @{model.submittedByUsername}
                    </AppText>
                  </Link>
                </Group>
              )}
              <Group position="apart" spacing="xs" mb={4}>
                <AppText size="xs" color="dimmed">
                  Submitted on
                </AppText>
                <AppText size="xs" weight={600}>
                  {format(new Date(model.createdAt), 'MMM d, yyyy')}
                </AppText>
              </Group>
              <Group position="apart" spacing="xs" mb={4}>
                <AppText size="xs" color="dimmed">
                  Status
                </AppText>
                <AppBadge size="xs" color={model.status === 1 ? 'green' : model.status === 0 ? 'yellow' : 'red'}>
                  {STATUS_LABEL[model.status] ?? 'Unknown'}
                </AppBadge>
              </Group>
              {model.approvedAt && (
                <Group position="apart" spacing="xs" mb={4}>
                  <AppText size="xs" color="dimmed">
                    Approved on
                  </AppText>
                  <AppText size="xs" weight={600}>
                    {format(new Date(model.approvedAt), 'MMM d, yyyy')}
                  </AppText>
                </Group>
              )}
            </Box>
          </AppCard>

          {isAdminOrMod && (
            <AppCard withBorder padding="md">
              <AppText
                size="xs"
                color="dimmed"
                sx={{
                  fontFamily: 'DM Sans, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  marginBottom: 8
                }}
              >
                ADMIN TOOLS
              </AppText>
              {model.status === 0 && (
                <>
                  <AppButton
                    sx={{
                      background: 'var(--gs-surface2)',
                      border: '1.5px solid var(--gs-border-bright)',
                      color: 'var(--gs-green)',
                      width: '100%',
                      padding: 8,
                      borderRadius: 5,
                      fontSize: 12,
                      textAlign: 'left',
                      marginBottom: 6
                    }}
                    onClick={handleApprove}
                  >
                    Approve Model
                  </AppButton>
                  <AppButton
                    sx={{
                      background: 'var(--gs-surface2)',
                      border: '1.5px solid var(--gs-border-bright)',
                      color: 'var(--gs-red)',
                      width: '100%',
                      padding: 8,
                      borderRadius: 5,
                      fontSize: 12,
                      textAlign: 'left',
                      marginBottom: 6
                    }}
                    onClick={() => setRejectModalOpen(true)}
                  >
                    Reject Submission
                  </AppButton>
                  <AppButton
                    sx={{
                      background: 'var(--gs-surface2)',
                      border: '1.5px solid var(--gs-border-bright)',
                      color: 'var(--gs-red)',
                      width: '100%',
                      padding: 8,
                      borderRadius: 5,
                      fontSize: 12,
                      textAlign: 'left',
                      marginBottom: 6
                    }}
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    Delete Submission
                  </AppButton>
                </>
              )}
              {model.status === 2 && userStore.isAdmin && (
                <AppButton
                  sx={{
                    background: 'var(--gs-surface2)',
                    border: '1.5px solid var(--gs-border-bright)',
                    color: 'var(--gs-red)',
                    width: '100%',
                    padding: 8,
                    borderRadius: 5,
                    fontSize: 12,
                    textAlign: 'left',
                    marginBottom: 6
                  }}
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  Delete Submission
                </AppButton>
              )}
              {model.status === 1 && (
                <>
                  <AppButton
                    sx={{
                      background: 'var(--gs-surface2)',
                      border: '1.5px solid var(--gs-border-bright)',
                      color: 'var(--gs-text)',
                      width: '100%',
                      padding: 8,
                      borderRadius: 5,
                      fontSize: 12,
                      textAlign: 'left',
                      marginBottom: 6
                    }}
                    onClick={() => setAdminEditOpen((v) => !v)}
                  >
                    Edit Model Details
                  </AppButton>
                  <AppButton
                    sx={{
                      background: 'var(--gs-surface2)',
                      border: '1.5px solid var(--gs-border-bright)',
                      color: 'var(--gs-red)',
                      width: '100%',
                      padding: 8,
                      borderRadius: 5,
                      fontSize: 12,
                      textAlign: 'left',
                      marginBottom: 6
                    }}
                    onClick={() => setHideModalOpen(true)}
                  >
                    Hide Model
                  </AppButton>
                  <AppButton
                    sx={{
                      background: 'var(--gs-surface2)',
                      border: '1.5px solid var(--gs-border-bright)',
                      color: 'var(--gs-text)',
                      width: '100%',
                      padding: 8,
                      borderRadius: 5,
                      fontSize: 12,
                      textAlign: 'left',
                      marginBottom: 6
                    }}
                    onClick={async () => {
                      if (!id) return
                      try {
                        if (model.isVerified) await agent.FirearmModels.unverify(id)
                        else await agent.FirearmModels.verify(id)
                        await loadModel(id)
                      } catch {
                        // toast via interceptor
                      }
                    }}
                  >
                    {model.isVerified ? 'Remove Verification' : 'Mark as Verified'}
                  </AppButton>
                </>
              )}
            </AppCard>
          )}
        </Box>
      </Box>

      {/* Related Models */}
      {relatedModels.length > 0 && (
        <Box mt="xl">
          <AppText
            size="xs"
            color="dimmed"
            sx={{ fontFamily: 'DM Sans, sans-serif', textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 600, marginBottom: 12 }}
          >
            Related Models
          </AppText>
          <SimpleGrid
            cols={2}
            breakpoints={[{ minWidth: 640, cols: 3 }, { minWidth: 960, cols: 6 }]}
            spacing="sm"
          >
            {relatedModels.map((r) => (
              <AppCard
                key={r.id}
                component={Link}
                to={`/registry/${r.id}`}
                withBorder
                padding="sm"
                radius="md"
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'border-color 150ms ease, box-shadow 150ms ease',
                  '&:hover': {
                    borderColor: 'var(--gs-accent)',
                    boxShadow: '0 4px 16px rgba(232,93,4,.15)'
                  }
                }}
              >
                {r.coverImageUrl
                  ? (
                  <AppCard.Section mb="xs">
                    <img
                      src={r.coverImageUrl}
                      alt=""
                      style={{ width: '100%', height: 72, objectFit: 'cover' }}
                    />
                  </AppCard.Section>
                    )
                  : (
                  <Box
                    sx={{
                      height: 72,
                      marginBottom: 8,
                      background: 'var(--gs-surface2)',
                      borderRadius: 6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#30363D'
                    }}
                  >
                    <IoImageOutline size={24} />
                  </Box>
                    )}
                <AppText size="xs" weight={600} lineClamp={1}>
                  {r.make} {r.model}
                </AppText>
                {r.caliber && (
                  <AppText size="xs" color="dimmed" lineClamp={1}>{r.caliber}</AppText>
                )}
                {r.isVerified && (
                  <AppText size="xs" sx={{ color: 'var(--gs-green)' }}>✓</AppText>
                )}
              </AppCard>
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* Admin edit modal */}
      <AppModal
        opened={adminEditOpen}
        onClose={() => setAdminEditOpen(false)}
        title="Edit model details"
        size="lg"
      >
        {model && (
          <Formik<FirearmModelFormValues>
            initialValues={{
              make: model.make ?? '',
              model: model.model ?? '',
              platform: model.platform ?? '',
              caliber: model.caliber ?? '',
              actionType: model.actionType ?? '',
              yearIntroduced: model.yearIntroduced ?? null,
              description: model.description ?? '',
              coverImageUrl: model.coverImageUrl ?? ''
            }}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                await updateModel(model.id, values)
                toast.success('Model updated')
                setAdminEditOpen(false)
                await loadModel(model.id)
              } catch {
                // toast via interceptor
              } finally {
                setSubmitting(false)
              }
            }}
          >
            {({ values, handleChange, isSubmitting }) => (
              <Form>
                <AppStack spacing="sm">
                  <AppInput label="Make" name="make" value={values.make} onChange={handleChange} required />
                  <AppInput label="Model" name="model" value={values.model} onChange={handleChange} required />
                  <AppInput label="Platform" name="platform" value={values.platform ?? ''} onChange={handleChange} />
                  <AppInput label="Caliber" name="caliber" value={values.caliber ?? ''} onChange={handleChange} />
                  <AppInput label="Action type" name="actionType" value={values.actionType ?? ''} onChange={handleChange} />
                  <AppInput
                    label="Year introduced"
                    name="yearIntroduced"
                    type="number"
                    value={values.yearIntroduced ?? ''}
                    onChange={handleChange}
                  />
                  <AppTextarea label="Description" name="description" value={values.description ?? ''} onChange={handleChange} minRows={3} />
                  <AppInput label="Cover image URL" name="coverImageUrl" value={values.coverImageUrl ?? ''} onChange={handleChange} />
                  <Group position="right" mt="sm">
                    <AppButton variant="default" onClick={() => setAdminEditOpen(false)}>Cancel</AppButton>
                    <AppButton type="submit" loading={isSubmitting}>Save</AppButton>
                  </Group>
                </AppStack>
              </Form>
            )}
          </Formik>
        )}
      </AppModal>

      {/* Delete confirm modal */}
      <AppModal
        opened={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete submission"
      >
        <AppText size="sm" mb="md">
          Permanently delete &ldquo;{model?.make} {model?.model}&rdquo;? This cannot be undone.
        </AppText>
        <Group position="right" spacing="xs">
          <AppButton variant="default" onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </AppButton>
          <AppButton
            color="red"
            onClick={async () => {
              if (!id) return
              try {
                await deleteModel(id)
                setDeleteConfirmOpen(false)
                history.push('/registry')
              } catch {
                // toast via interceptor
              }
            }}
          >
            Delete
          </AppButton>
        </Group>
      </AppModal>

      {/* Reject modal */}
      <AppModal
        opened={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        title="Reject submission"
      >
        <AppText size="sm" mb="xs">
          Add an optional reason for rejection:
        </AppText>
        <AppTextarea
          minRows={3}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.currentTarget.value)}
        />
        <Group position="right" mt="md" spacing="xs">
          <AppButton variant="default" onClick={() => setRejectModalOpen(false)}>
            Cancel
          </AppButton>
          <AppButton color="red" onClick={handleRejectConfirm}>
            Reject
          </AppButton>
        </Group>
      </AppModal>

      <AppModal
        opened={coverModalOpen}
        onClose={() => {
          setPendingCoverImageFile(null)
          setCoverModalOpen(false)
        }}
        title="Change cover image"
      >
        <AppText size="xs" color="dimmed" mb="sm">
          Use a photo you own or have rights to. Registry content is subject to moderation.
        </AppText>
        <LegalConsentCheckbox
          legalTextKey="community-guidelines"
          checked={coverConsentGiven}
          onChange={setCoverConsentGiven}
        />
        <Group align="flex-start" spacing="md" grow mt="md">
          <Paper
            component="button"
            type="button"
            onClick={() => setCoverUrlSectionOpen((v) => !v)}
            sx={{
              cursor: 'pointer',
              border: '1px solid var(--gs-border)',
              borderRadius: 12,
              width: 132,
              height: 132,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: 8,
              background: 'var(--gs-surface)',
              color: 'var(--gs-muted)'
            }}
          >
            <AppText size="xs" weight={500}>
              {coverUrlSectionOpen ? 'Hide URL field' : 'Set image URL'}
            </AppText>
          </Paper>
          <MobileImageCapture
            onFile={handleCoverFileSelected}
            accept="image/jpeg,image/png,image/webp"
            loading={changingCover}
            disabled={changingCover}
            label="Upload image"
            variant="light"
            fullWidth
          />
        </Group>
        <Collapse in={coverUrlSectionOpen}>
          <AppStack spacing="sm" mt="md">
            <AppInput
              label="Image URL"
              description="Direct https link. Leave empty and apply to remove the cover."
              placeholder="https://..."
              value={coverUrlDraft}
              onChange={(e) => setCoverUrlDraft(e.currentTarget.value)}
            />
            <Group position="right" spacing="xs">
              <AppButton variant="default" onClick={() => setCoverUrlSectionOpen(false)}>
                Close URL field
              </AppButton>
              <AppButton onClick={applyCoverUrl} loading={changingCover}>
                Apply URL
              </AppButton>
            </Group>
          </AppStack>
        </Collapse>
      </AppModal>

      <SerialBlurModal
        file={pendingCoverImageFile}
        onConfirm={doCoverUpload}
        onSkip={doCoverUpload}
        onClose={() => setPendingCoverImageFile(null)}
      />

      {/* Hide modal */}
      <AppModal
        opened={hideModalOpen}
        onClose={() => setHideModalOpen(false)}
        title="Hide model"
      >
        <AppText size="sm" mb="xs">
          Add an optional reason for hiding:
        </AppText>
        <AppTextarea
          minRows={3}
          value={hideReason}
          onChange={(e) => setHideReason(e.currentTarget.value)}
        />
        <Group position="right" mt="md" spacing="xs">
          <AppButton variant="default" onClick={() => setHideModalOpen(false)}>
            Cancel
          </AppButton>
          <AppButton color="red" onClick={handleHideConfirm}>
            Hide
          </AppButton>
        </Group>
      </AppModal>

      <ReportContentModal
        opened={reportOpen}
        onClose={() => setReportOpen(false)}
        targetType={FlagTargetType.FirearmModel}
        targetId={model.id}
        contentLabel="firearm model"
      />
    </MainContentInset>
  )
})
